import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const ERRORS = {
  invalid_activation: 'Invalid code',
  code_expired: 'Code expired',
  already_used: 'Code already used',
  too_many_attempts: 'Too many attempts',
  club_not_local_first: 'Prepare Local POS migration in Super Admin first',
  device_revoked: 'Device revoked',
  device_bound_elsewhere: 'Device revoked',
  cloud_unavailable: 'Internet connection is required for the first activation.',
  protected_state_unavailable: 'Protected local state unavailable. Contact an administrator.',
  already_bound: 'This POS is already activated.',
};

const post = async (path, body) => {
  const token = localStorage.getItem('admin_token');
  const r = await fetch(`${API}/api/pos/auth/local/${path}`, { method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || 'cloud_unavailable');
  return data;
};

export const LocalActivation = ({ initial }) => {
  const [info, setInfo] = useState(initial);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const [role, setRole] = useState(null);
  const [migration, setMigration] = useState(null);
  useEffect(() => {
    if (!info.bound) return;
    const load = () => fetch(`${API}/api/mode-migration/local/status`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null).then(setMigration).catch(() => {});
    load(); const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [info.bound]);
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(user => setRole(user?.role)).catch(() => {});
  }, []);

  const activate = async e => {
    e.preventDefault();
    setBusy(true); setError(''); setIncomplete(false);
    try {
      const data = await post('activate', { code });
      setInfo(data);
      if (!data.bound) setIncomplete(true);
    } catch (err) {
      setError(ERRORS[err.message] || ERRORS.cloud_unavailable);
    } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true); setError('');
    try {
      const data = await post('complete', {});
      setInfo(data);
      setIncomplete(!data.bound);
    } catch { setError(ERRORS.cloud_unavailable); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true); setError('');
    try { setInfo(await post('refresh', {})); } catch { setError('Холболт эсвэл админ эрхийг шалгаад дахин оролдоно уу.'); }
    finally { setBusy(false); }
  };

  return <section className="space-y-6" data-testid="local-activation-panel">
    {info.bound ? <>
      <h2 className="text-lg font-semibold" data-testid="local-activation-club">{info.club_name || 'RENTOS Local POS'}</h2>
      <p data-testid="local-activation-bound" role="status">{!info.available ? 'Хамгаалагдсан төлөв нээгдсэнгүй' : migration?.ready ? 'Local POS ready · configure and operate locally' : 'Device activated · waiting for Local setup / Super Admin verification'}</p>
      {migration?.gate?.locked && <p data-testid="local-migration-maintenance" role="status" className="text-sm text-amber-600">Local operations are locked. Keep this POS online to complete the approved migration.</p>}
      <p data-testid="local-activation-last-refresh" className="text-sm">Сүүлд шинэчилсэн: {info.last_success ? new Date(info.last_success * 1000).toLocaleString() : '—'}</p>
      {info.error === 'device_revoked' && <p data-testid="local-activation-revoked" role="alert" className="text-sm text-red-500">Local POS authorization has been revoked. Contact an administrator.</p>}
      {info.error && info.error !== 'device_revoked' && <p data-testid="local-activation-refresh-warning" role="status" className="text-sm text-amber-600">Шинэчлэлт амжилтгүй. Өмнөх хүчинтэй нэвтрэх эрх хадгалагдсан.</p>}
      {['admin', 'club_admin'].includes(role) && <Button data-testid="local-activation-refresh" disabled={busy || !info.available} onClick={refresh}><RefreshCw size={16} className={`mr-2 ${busy ? 'animate-spin' : ''}`} />Эрх шинэчлэх</Button>}
    </> : <>
      <h2 className="text-lg font-semibold" data-testid="local-activation-title">Activate RENTOS Local POS</h2>
      {incomplete ? <div className="space-y-3" data-testid="local-activation-incomplete">
        <p role="alert" className="text-sm text-amber-600">Device is registered but provisioning is incomplete. Reconnect and retry — no new code is needed.</p>
        <Button data-testid="local-activation-resume" disabled={busy} onClick={resume}><RefreshCw size={16} className={`mr-2 ${busy ? 'animate-spin' : ''}`} />Retry provisioning</Button>
      </div> : <form className="space-y-4" data-testid="local-activation-form" onSubmit={activate}>
        <label className="block text-sm">6-digit activation code
          <Input data-testid="local-activation-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            className="font-mono text-2xl tracking-widest text-center" value={code} disabled={busy || !info.available}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        </label>
        <Button data-testid="local-activation-submit" type="submit" disabled={busy || code.length !== 6 || !info.available}>{busy ? 'Activating…' : 'Activate'}</Button>
      </form>}
      {!info.available && <p data-testid="local-activation-unavailable" role="alert" className="text-sm text-red-500">{ERRORS.protected_state_unavailable}</p>}
    </>}
    {error && <p data-testid="local-activation-error" role="alert" className="text-sm text-red-500">{error}</p>}
    <Link to="/" data-testid="local-activation-login-link" className="block underline underline-offset-4">Нэвтрэх</Link>
  </section>;
};
